/**
 * useScenarios — CRUD against `buying_session_scenarios`.
 *
 * Day 54 Phase 4 architecture:
 * - RLS handles owner scoping (`owner_user_id = auth.uid()`).
 * - This hook adds an application-level distributor filter on top: scenarios
 *   visible in a session are the user's global scenarios (distributor_id IS
 *   NULL) plus scenarios scoped to the active distributor. Switching
 *   distributors reshuffles the visible list without a refetch.
 * - The distributor code is fetched via PostgREST embed
 *   `select('*, distributor:distributor_id(code)')` so the picker can render
 *   labels like "Conservative · BLVS" without a second round-trip.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useApp } from './useApp';
import type { Json } from '../lib/database.types';

export interface Scenario {
  id: string;
  name: string;
  distributor_id: string | null;
  distributor_code: string | null;
  filters: Record<string, string | undefined>;
  weights: Record<string, { enabled: boolean; weight: string } | undefined>;
  constraints: Record<string, string | undefined>;
  created_at: string;
  updated_at: string;
}

interface SaveScenarioArgs {
  name: string;
  distributorId: string | null;
  filters: Scenario['filters'];
  weights: Scenario['weights'];
  constraints: Scenario['constraints'];
}

export interface UseScenariosResult {
  scenarios: Scenario[];
  loading: boolean;
  error: string | null;
  saveScenario(args: SaveScenarioArgs): Promise<Scenario>;
  deleteScenario(id: string): Promise<void>;
  renameScenario(id: string, name: string): Promise<void>;
  refetch(): Promise<void>;
}

interface RawScenarioRow {
  id: string;
  name: string;
  distributor_id: string | null;
  filters: unknown;
  weights: unknown;
  constraints: unknown;
  created_at: string;
  updated_at: string;
  distributor: { code: string } | null;
}

function mapRow(r: RawScenarioRow): Scenario {
  return {
    id: r.id,
    name: r.name,
    distributor_id: r.distributor_id,
    distributor_code: r.distributor?.code ?? null,
    filters: (r.filters || {}) as Scenario['filters'],
    weights: (r.weights || {}) as Scenario['weights'],
    constraints: (r.constraints || {}) as Scenario['constraints'],
    created_at: r.created_at,
    updated_at: r.updated_at,
  };
}

export function useScenarios(activeDistributorId: string | null): UseScenariosResult {
  const { user } = useApp();
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchScenarios = useCallback(async (): Promise<void> => {
    if (!user?.id) {
      setScenarios([]);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase
      .from('buying_session_scenarios')
      .select('*, distributor:distributor_id(code)')
      .order('name');
    setLoading(false);
    if (e) {
      setError(e.message);
      return;
    }
    const rows = (data as unknown as RawScenarioRow[]) || [];
    // Application-level distributor filter on top of RLS owner scope.
    const filtered = rows.filter(
      (r) => r.distributor_id === null || r.distributor_id === activeDistributorId
    );
    setScenarios(filtered.map(mapRow));
  }, [user?.id, activeDistributorId]);

  useEffect(() => {
    void fetchScenarios();
  }, [fetchScenarios]);

  const saveScenario = useCallback(
    async (args: SaveScenarioArgs): Promise<Scenario> => {
      if (!user?.id) throw new Error('Cannot save scenario: no authenticated user');
      const trimmed = args.name.trim();
      if (!trimmed) throw new Error('Scenario name is required');
      const { data, error: e } = await supabase
        .from('buying_session_scenarios')
        .insert({
          owner_user_id: user.id,
          distributor_id: args.distributorId,
          name: trimmed,
          filters: args.filters as unknown as Json,
          weights: args.weights as unknown as Json,
          constraints: args.constraints as unknown as Json,
        } as never)
        .select('*, distributor:distributor_id(code)')
        .single();
      if (e || !data) throw new Error(e?.message || 'saveScenario failed');
      const row = mapRow(data as unknown as RawScenarioRow);
      // Refetch keeps ordering + visibility filter consistent.
      await fetchScenarios();
      return row;
    },
    [user?.id, fetchScenarios]
  );

  const deleteScenario = useCallback(
    async (id: string): Promise<void> => {
      const { error: e } = await supabase.from('buying_session_scenarios').delete().eq('id', id);
      if (e) throw new Error(e.message);
      await fetchScenarios();
    },
    [fetchScenarios]
  );

  const renameScenario = useCallback(
    async (id: string, name: string): Promise<void> => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error('Scenario name is required');
      const { error: e } = await supabase
        .from('buying_session_scenarios')
        .update({ name: trimmed } as never)
        .eq('id', id);
      if (e) throw new Error(e.message);
      await fetchScenarios();
    },
    [fetchScenarios]
  );

  return {
    scenarios,
    loading,
    error,
    saveScenario,
    deleteScenario,
    renameScenario,
    refetch: fetchScenarios,
  };
}
